import { TranslatedMeeting } from 'types/translations';

import { MeetingTranslationActions } from 'actions/actionTypes';
import decodeHtmlEntities from 'helpers/decodeHtmlEntities';

const decodeSlateTextNodes = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => decodeSlateTextNodes(item));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => {
      if (key === 'text' && typeof nestedValue === 'string') {
        return [key, decodeHtmlEntities(nestedValue)];
      }

      if (key === 'children' || key === 'content') {
        return [key, decodeSlateTextNodes(nestedValue)];
      }

      return [key, nestedValue];
    })
  );
};

const decodeTranslatedNotes = (
  translatedNotes: TranslatedMeeting['translatedNotes']
): TranslatedMeeting['translatedNotes'] => {
  if (Array.isArray(translatedNotes)) {
    return decodeSlateTextNodes(translatedNotes) as TranslatedMeeting['translatedNotes'];
  }

  if (!translatedNotes || typeof translatedNotes !== 'object') {
    return translatedNotes;
  }

  const decodedNotes = {
    ...translatedNotes
  };

  if (typeof decodedNotes.textSlate === 'string') {
    try {
      decodedNotes.textSlate = JSON.stringify(
        decodeSlateTextNodes(JSON.parse(decodedNotes.textSlate))
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse/decode textSlate:', err, decodedNotes.textSlate);
      decodedNotes.textSlate = '';
    }
  }

  return decodedNotes;
};

const decodeTranslatedTranscript = (
  translatedTranscript: TranslatedMeeting['translatedTranscript']
): NonNullable<TranslatedMeeting['translatedTranscript']> => {
  if (!Array.isArray(translatedTranscript)) {
    return [];
  }

  return translatedTranscript.map(chunk => {
    if (!chunk || typeof chunk !== 'object') {
      return chunk;
    }

    const transcriptChunk = chunk as Record<string, unknown>;

    return {
      ...transcriptChunk,
      transcript:
        typeof transcriptChunk.transcript === 'string'
          ? decodeHtmlEntities(transcriptChunk.transcript)
          : transcriptChunk.transcript
    };
  });
};

type State = {
  data: Record<
    string,
    {
      fetchingNotes: boolean;
      fetchingTranscript: boolean;
      data: TranslatedMeeting | null;
    }
  >;
};

export const translationInitialState: State = {
  data: {}
};

const translations = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: State = translationInitialState,
  action: {
    type: string;
    meetingUuid: string;
    data: any;
  }
): State => {
  switch (action.type) {
    case MeetingTranslationActions.FETCH_MEETING_TRANSLATION:
      return {
        ...state,
        data: {
          ...state.data,
          [action.meetingUuid]: {
            fetchingNotes: true,
            fetchingTranscript: true,
            data: null
          }
        }
      };
    case MeetingTranslationActions.FETCH_MEETING_TRANSLATION_NOTES_SUCCESS: {
      return {
        ...state,
        data: {
          ...state.data,
          [action.meetingUuid]: {
            ...state.data?.[action.meetingUuid],
            fetchingNotes: false,
            data: {
              ...state.data?.[action.meetingUuid]?.data,
              translatedNotes: decodeTranslatedNotes(action.data.translatedNotes),
              speakers: action.data.speaker
            }
          }
        }
      };
    }
    case MeetingTranslationActions.FETCH_MEETING_TRANSLATION_TRANSCRIPT_SUCCESS: {
      return {
        ...state,
        data: {
          ...state.data,
          [action.meetingUuid]: {
            ...state.data?.[action.meetingUuid],
            fetchingTranscript: false,
            data: {
              ...state.data?.[action.meetingUuid]?.data,
              translatedTranscript: [
                ...(state.data?.[action.meetingUuid]?.data
                  ?.translatedTranscript ?? []),
                ...decodeTranslatedTranscript(action.data?.translatedTranscript)
              ]
            }
          }
        }
      };
    }
    case MeetingTranslationActions.FETCH_MEETING_TRANSLATION_ERROR:
      return {
        ...state,
        data: {
          ...state.data,
          [action.meetingUuid]: {
            fetchingNotes: false,
            fetchingTranscript: false,
            data: null
          }
        }
      };
    default:
      return state;
  }
};

export default translations;
